import { createElement } from "react";
import { createRoute, lazyRouteComponent } from "@tanstack/react-router";

import { requirePermission } from "../../../../shared/routing/requirePermission";
import {
  DEPARTMENTS_CREATE,
  DEPARTMENTS_UPDATE,
  DEPARTMENTS_VIEW,
} from "../../organizationPermissions";
import {
  archivedDepartmentsSearchSchema,
  departmentsSearchSchema,
} from "./searchSchemas";
import type { OrganizationRouteFactory } from "./organizationRouteContext";

const DepartmentsListScreen = lazyRouteComponent(
  () => import("../DepartmentsListScreen"),
  "DepartmentsListScreen",
);
const ArchivedDepartmentsListScreen = lazyRouteComponent(
  () => import("../ArchivedDepartmentsListScreen"),
  "ArchivedDepartmentsListScreen",
);
const DepartmentCreateScreen = lazyRouteComponent(
  () => import("../DepartmentCreateScreen"),
  "DepartmentCreateScreen",
);
const DepartmentDetailScreen = lazyRouteComponent(
  () => import("../DepartmentDetailScreen"),
  "DepartmentDetailScreen",
);
const DepartmentEditScreen = lazyRouteComponent(
  () => import("../DepartmentEditScreen"),
  "DepartmentEditScreen",
);

export const createDepartmentsRoutes: OrganizationRouteFactory = ({
  context,
  departments,
}) => {
  // basePath is a runtime string, so the templates lose the literal type
  // TanStack Router needs to type params -- $id is guaranteed by the path
  // (same as createCompanyDetailRoutes).
  const detailRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/departments/$id`,
    beforeLoad: requirePermission([DEPARTMENTS_VIEW]),
    component: function DepartmentDetailRouteScreen() {
      const { id } = detailRoute.useParams() as unknown as { id: string };
      return createElement(DepartmentDetailScreen, {
        repository: departments,
        basePath: context.basePath,
        departmentId: id,
      });
    },
  });
  const editRoute = createRoute({
    getParentRoute: context.getParentRoute,
    path: `${context.basePath}/departments/$id/edit`,
    beforeLoad: requirePermission([DEPARTMENTS_UPDATE]),
    component: function DepartmentEditRouteScreen() {
      const { id } = editRoute.useParams() as unknown as { id: string };
      return createElement(DepartmentEditScreen, {
        repository: departments,
        basePath: context.basePath,
        departmentId: id,
      });
    },
  });

  return [
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/departments`,
      validateSearch: departmentsSearchSchema,
      beforeLoad: requirePermission([DEPARTMENTS_VIEW]),
      component: () =>
        createElement(DepartmentsListScreen, {
          repository: departments,
          basePath: context.basePath,
        }),
    }),
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/departments/archived`,
      validateSearch: archivedDepartmentsSearchSchema,
      beforeLoad: requirePermission([DEPARTMENTS_VIEW]),
      component: () =>
        createElement(ArchivedDepartmentsListScreen, {
          repository: departments,
          basePath: context.basePath,
        }),
    }),
    createRoute({
      getParentRoute: context.getParentRoute,
      path: `${context.basePath}/departments/create`,
      beforeLoad: requirePermission([DEPARTMENTS_CREATE]),
      component: () =>
        createElement(DepartmentCreateScreen, {
          repository: departments,
          basePath: context.basePath,
        }),
    }),
    editRoute,
    detailRoute,
  ];
};
